// ***Home.tsx

import React, { useState, useEffect } from 'react';
import skyBackground from '../skybg.webp';

export default function Home(): React.JSX.Element {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Hide the scroll indicator once the user starts scrolling down
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToNextSection = () => {
    window.scrollTo({
      top: window.innerHeight - 64, // Scroll past hero section height
      behavior: 'smooth',
    });
  };

  return (
    <div className="w-full bg-slate-950 text-slate-100">
      {/* 1. HERO SECTION */}
      <section
        className="relative min-h-[calc(100vh-4rem)] w-full bg-slate-950 bg-fixed bg-cover bg-center flex flex-col justify-between"
        style={{ backgroundImage: `url(${skyBackground})` }}
      >
        {/* Hero Content Container */}
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-12 text-center">
          <div className="max-w-2xl flex-1 flex flex-col items-center justify-center space-y-6 py-8 sm:py-12">

            <img
              src="/MCC_Logo_White.svg"
              alt="My Connective Care LLC"
              className="mx-auto mb-6 sm:mb-8 h-36 sm:h-48 w-auto object-contain"
            />

            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              Care. Connected.<br />Coming Soon.
            </h1>

            <p className="text-base sm:text-lg font-bold text-mcc-lightest px-2">
              Closing the gaps between Caregiving and Care Needs.
            </p>

            {/* Join Button */}
            <div className="pt-4 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsContactOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl sm:rounded-md border border-transparent bg-white px-8 py-4 sm:py-3 text-base font-semibold text-mcc-darkest transition-colors duration-200 hover:bg-mcc-lightest shadow-lg"
              >
                Join Our Care Team
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator (Bouncing Down Arrow) */}
        <div
          onClick={scrollToNextSection}
          className={`absolute bottom-6 left-1/2 -translate-x-1/2 cursor-pointer transition-opacity duration-500 z-20 flex flex-col items-center gap-1 text-white/80 hover:text-white ${
            showScrollIndicator ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <span className="text-xs font-semibold uppercase tracking-widest text-mcc-lightest drop-shadow">
            Scroll
          </span>
          <svg
            className="h-6 w-6 animate-bounce stroke-current"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </section>

      {/* 2. WHY WE'RE DIFFERENT SECTION */}
      <section className="w-full bg-slate-50 py-12 sm:py-20 px-4 sm:px-6 text-mcc-darkest">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-mcc-darkest">
            Why We're Different
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg font-medium text-slate-600 max-w-3xl mx-auto leading-relaxed md:text-xl px-2">
            We are not a marketplace. We are not an agency directory.<br className="hidden sm:inline" />
            <span className="font-semibold text-mcc-darkest block sm:inline mt-1 sm:mt-0">
              We are care coordination infrastructure.
            </span>
          </p>

          {/* Value Proposition Highlights */}
          <div className="mt-10 sm:mt-16 grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3 text-left">

            {/* Pillar 1: Real-Time Peace of Mind */}
            <div className="rounded-2xl sm:rounded-xl border border-mcc-lightest/60 bg-white p-6 sm:p-8 shadow-sm transition-all hover:border-mcc-light hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl sm:rounded-lg bg-mcc-lightest/30 text-mcc-darkest font-bold text-xl mb-4 sm:mb-6 border border-mcc-lightest/50">
                1
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-mcc-darkest">Real-Time Peace of Mind</h3>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                No more guessing how a visit went. Family members receive instant visit updates and completed care summaries so you stay connected and confident every step of the way.
              </p>
            </div>

            {/* Pillar 2: Chosen Caregivers, Guaranteed Backup */}
            <div className="rounded-2xl sm:rounded-xl border border-mcc-lightest/60 bg-white p-6 sm:p-8 shadow-sm transition-all hover:border-mcc-light hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl sm:rounded-lg bg-mcc-lightest/30 text-mcc-darkest font-bold text-xl mb-4 sm:mb-6 border border-mcc-lightest/50">
                2
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-mcc-darkest">Chosen Caregivers, Guaranteed Backup</h3>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                You select the primary caregivers who work in your home. To ensure you never miss a visit, your assigned Care Manager maintains pre-approved backup caregivers from the same supervised team who know your preferences in advance.
              </p>
            </div>

            {/* Pillar 3: Complete Care & Pricing Transparency */}
            <div className="rounded-2xl sm:rounded-xl border border-mcc-lightest/60 bg-white p-6 sm:p-8 shadow-sm transition-all hover:border-mcc-light hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl sm:rounded-lg bg-mcc-lightest/30 text-mcc-darkest font-bold text-xl mb-4 sm:mb-6 border border-mcc-lightest/50">
                3
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-mcc-darkest">Complete Care & Pricing Transparency</h3>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                Clear, upfront rates with no hidden fees or surprise surcharges. Paired with verified check-ins and clear care plan tracking, you always know exactly what support is provided and what you are paying for.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 3. INLINE CONTACT MODAL */}
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl sm:rounded-xl border border-mcc-lightest/40 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div className="text-left">
                <h2 className="text-xl font-semibold text-mcc-darkest">Join Our Care Team</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Interested in becoming a part of our team? We are looking for compassionate caregivers. Reach out to us via email and we'll get back to you with more information.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsContactOpen(false)}
                className="text-2xl leading-none text-slate-400 transition-colors hover:text-mcc-darkest p-1"
                aria-label="Close contact dialog"
              >
                ×
              </button>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-700 text-left">
              <p>
                <span className="font-semibold text-mcc-darkest">Email:</span>{' '}
                <a href="mailto:info@myconnectivecare.com" className="text-mcc-neutral font-medium underline hover:text-mcc-darkest">
                  info@myconnectivecare.com
                </a>
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsContactOpen(false)}
                className="w-full sm:w-auto rounded-xl sm:rounded-md bg-mcc-darkest px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-mcc-neutral"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
